import type { IntegrationKind } from '@developerz/domain';
import { type JSX, Show } from 'solid-js';
import { t } from '../i18n';
import type { IntegrationRow } from '../lib/integrations';
import { StatusPill, type StatusTone } from './StatusPill';
import { Button } from './ui/Button';

// One connector tile in the md:3 catalog grid (mock Integrations()): name +
// status chip over the description, action(s) in the footer band. `row` null =
// not connected. Presenter — connect / disconnect are handed up.

export type IntegrationCardProps = {
  readonly kind: IntegrationKind;
  readonly row: IntegrationRow | null;
  /** Whether this kind has an OAuth redirect connect wired this deployment (#599). */
  readonly oauthCapable: boolean;
  readonly onConnect: (kind: IntegrationKind) => void;
  /** Start the OAuth redirect connect (top-level navigation — see container). */
  readonly onConnectOauth: (kind: IntegrationKind) => void;
  readonly onDisconnect: (row: IntegrationRow) => void;
};

const TONE: Record<string, StatusTone> = { connected: 'success', error: 'danger' };

export function IntegrationCard(props: IntegrationCardProps): JSX.Element {
  const status = (): string => props.row?.status ?? 'disconnected';
  return (
    <li class="card card--footered integration-card" data-kind={props.kind} data-status={status()}>
      {/* divs, not header/footer: outside sectioning content those map to
          banner/contentinfo and duplicate the shell's landmarks (axe). */}
      <div class="card__header">
        <div class="integration-card__head">
          <h2 class="card__title">{t(`integrations.kind.${props.kind}`)}</h2>
          <StatusPill tone={TONE[status()] ?? 'neutral'}>
            {t(`integrations.status_${status()}`)}
          </StatusPill>
        </div>
        <p class="card__desc">{t(`integrations.desc.${props.kind}`)}</p>
      </div>

      <div class="card__footer integration-card__actions">
        <Show
          when={props.row}
          fallback={
            <>
              <Show when={props.oauthCapable}>
                <Button size="sm" onClick={() => props.onConnectOauth(props.kind)}>
                  {t('integrations.connect_with', { name: t(`integrations.kind.${props.kind}`) })}
                </Button>
              </Show>
              <Button
                size="sm"
                variant={props.oauthCapable ? 'outline' : 'primary'}
                onClick={() => props.onConnect(props.kind)}
              >
                {t(props.oauthCapable ? 'integrations.paste_credentials' : 'integrations.connect')}
              </Button>
            </>
          }
        >
          {(row) => (
            <Button variant="outline" size="sm" onClick={() => props.onDisconnect(row())}>
              {t('integrations.disconnect')}
            </Button>
          )}
        </Show>
      </div>
    </li>
  );
}
